import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";
import { useLoading } from "../../context/LoadingContext";
import { useRiskManagerStore } from "../../store/useRiskManagerStore";
import { fetchAuthSession } from "aws-amplify/auth";
import { RegulifyService } from "../../api/RegulifyService";
import { useState } from "react";
import { buildWsUrl } from "../../utils/wsUrl";

const WS_PING_INTERVAL_MS = 20000;

const WS_ERROR_MESSAGE = "There was a problem processing your Risk Report generation request. Please try again later.";

const UNHANDELLED_ERROR_MESSAGE = "Unhandelled Error fulfilling the request: "

const WS_URL_GEN_RISK_REPORT = (token) =>  buildWsUrl("generate_risk_report", token);

const getAuthToken = async () => {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
};

const startKeepAlive = (ws) =>
  setInterval(() => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "ping" }));
    }
  }, WS_PING_INTERVAL_MS);

function handleParsedMessage(parsed, { setLoading }) {
  switch (parsed.type) {
    case "error":
      console.error("Processing error:", parsed.message);
      toast.error(`Error: ${parsed.message}`);
      setLoading(false);
      return true;

    case "info":
      console.info("Processing update:", parsed.message);
      toast.info(`Update: ${parsed.message}`);
      return true;

    case "pong":
      console.info("Received ping/pong reply:", parsed.message);
      return true;

    default:
      return false;
  }
}

const RiskReport = () => {
  const { setLoading } = useLoading();
  const form = useRiskManagerStore(useShallow((state) => state.form));
  const [selectedRiskControlFiles, setSelectedRiskControlFiles] = useState([],);

  const handleCreateRiskReportClick = async () => {
    
    setLoading(true,"Processing your file… This may take several minutes. Thank you for waiting.", "text-green-600");

    if (!selectedRiskControlFiles?.length) {
          toast.warning("Please select at least one file.");
          return;
        }
    
        const formData = new FormData();
        for (let i = 0; i < selectedRiskControlFiles.length && i < 5; i++) {
            formData.append("updatedRiskFile", selectedRiskControlFiles[i]);
          }
    
        formData.append("llm_to_be_used", "gpt");
        formData.append("device_name", form["device_name"]);
        formData.append("override_type", 3);
    
        let docPath;
    
        try {
          setLoading(true, "Processing your file… This may take several minutes. Thank you for waiting.", "text-green-600");
    
          const response = await RegulifyService.riskmgr_upload_override_files(formData);
    
          if (response.status !== 200) {
            throw new Error("Failed to upload document. Try again.");
          }
    
          docPath = response.data.risk_file_override_path;
        } catch (error) {
          setLoading(false);
          console.error("Error during uploading file:", error);
          toast.error("Error during uploading file");
          return;
        }
    
        if (!docPath) return;

    let keepAliveInterval;

    try {
      const token = await getAuthToken();

      const ws = new WebSocket(WS_URL_GEN_RISK_REPORT(token));
      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        keepAliveInterval = startKeepAlive(ws);

        const payload = {
          device_name: form["device_name"],
          device_code: form["device_code"],
          device_type: form["device_type"],
          override_flag: 1,
        };

        ws.send(JSON.stringify(payload));
        toast.info("Processing started...");
      };

      ws.onmessage = (event) => {
        const { data } = event;

        try {
          const parsed = JSON.parse(data);

          if (handleParsedMessage(parsed, { setLoading })) { return; }

          if (parsed.content && parsed.filename) {
              const byteCharacters = atob(parsed.content);
              const byteArray = new Uint8Array(
                [...byteCharacters].map((c) => c.charCodeAt(0))
              );

              const blob = new Blob([byteArray], {
                type:
                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
              });

              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = parsed.filename;
              link.click();           
          } else {
            console.log("Unexpected message format:", parsed);
          }
        } catch {
          console.log("Non-JSON message received:", data);
        }
      };

      ws.onerror = () => {
        console.error(WS_ERROR_MESSAGE);
        toast.error(WS_ERROR_MESSAGE);
        setLoading(false);
      };

      ws.onclose = () => {
        console.log("WebSocket closed");
        clearInterval(keepAliveInterval);
        setLoading(false);
      };
    } catch (error) {
      console.error(UNHANDELLED_ERROR_MESSAGE, error);
      setLoading(false);
    }
  };

  return (
    <div className="pl-4">
      <div className="mt-4 flex flex-col gap-4">
        <div className="form-control w-full max-w-md">
          <label className="label">
            <span className="label-text font-semibold">
              Select Risk Control XLX file (generated in previous step)
            </span>
          </label>
          <input
            type="file"
            accept=".xlsx"
            id="updatedRiskControlFile"
            className="file-input file-input-bordered w-full"
            onChange={(e) => setSelectedRiskControlFiles(e.target.files)}
          />
          <p className="mt-1 mb-1 text-sm text-gray-500">
            Accepted formats: <span className="font-medium">.xlsx</span>
          </p>
        </div>
        </div>
      <button
        className="btn btn-primary tooltip tooltip-right mt-12 w-fit rounded px-4 py-2"
        data-tip="Download the compiled Risk Report in Word format"
        onClick={handleCreateRiskReportClick}
      >
        Create Risk Report
      </button>
    </div>
  );
};
export default RiskReport;
